import { css } from 'aphrodite';
import * as React from 'react';
import { NavLink, useHistory } from 'react-router-dom';
import { FilterOperator, IAccount } from '../../../../extViewmodels';
import { useAdminAccountFilterQuery } from '../../../../queries/Admin/Account/useAdminAccountFilterQuery';
import { useAdminAccountQuery } from '../../../../queries/Admin/Account/useAdminAccountQuery';
import { AccountFilterCriteriaProperty } from '../../../../viewmodels/AdminViewModels';
import { Button } from '../../../../web/components-v2/Button';
import {
	Combobox,
	ComboboxContent,
	ComboboxEmpty,
	ComboboxInput,
	ComboboxItem,
	ComboboxList,
	ComboboxStatus,
	ComboboxTrigger,
} from '../../../../web/components-v2/Combobox';
import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbLink,
	BreadcrumbList,
	BreadcrumbSeparator,
} from '../../../../web/components/Breadcrumb';
import { ChevronDown } from '../../../../web/components/svgs/icons/ChevronDown';
import { bs } from '../../../../web/styles/styles';
import { getSessionAdminAccountsUrl } from '../Accounts';
import { s } from './accountDetailsBreadcrumbStyles';

interface AccountDetailsBreadcrumbProps {
	accountId: string;
	getAccountPageUrl?: (accountId: string) => string;
}

const defaultGetAccountPageUrl = (accountId: string) => `/accounts/${accountId}`;

export function AccountDetailsBreadcrumb({
	accountId,
	getAccountPageUrl = defaultGetAccountPageUrl,
}: AccountDetailsBreadcrumbProps) {
	const latestAdminAccountsUrl = React.useMemo(getSessionAdminAccountsUrl, []);
	const history = useHistory();
	const [searchValue, setSearchValue] = React.useState('');

	const accountQuery = useAdminAccountQuery({ accountId, enabled: !!accountId });
	const accountName = accountQuery.data?.companyName ?? 'Loading...';

	const accountFilterQuery = useAdminAccountFilterQuery({
		filter: {
			criteria: [{ property: AccountFilterCriteriaProperty.Name, value: searchValue }],
			op: FilterOperator.And,
		},
		autocompleteFragment: searchValue || undefined,
		pageSize: 15,
	});

	const accounts = React.useMemo(() => {
		return accountFilterQuery.data?.pages?.flatMap(page => page.values ?? []) ?? [];
	}, [accountFilterQuery.data]);

	return (
		<Breadcrumb>
			<BreadcrumbList>
				<BreadcrumbItem>
					<BreadcrumbLink asChild>
						<NavLink to={latestAdminAccountsUrl}>Accounts</NavLink>
					</BreadcrumbLink>
				</BreadcrumbItem>
				<BreadcrumbSeparator />
				<BreadcrumbItem>
					<Combobox
						items={accounts}
						value={null}
						inputValue={searchValue}
						onInputValueChange={setSearchValue}
						onValueChange={(value: IAccount) => {
							if (value?.id) {
								history.push(getAccountPageUrl(value.id));
							}
						}}
					>
						<ComboboxTrigger
							render={({ className, ...props }) => (
								<Button variant='ghost' styles={[s.accountSwitcherTrigger]} {...props}>
									{accountName}
									<ChevronDown />
								</Button>
							)}
						/>
						<ComboboxContent styles={[s.comboboxContent]}>
							<div className={css(bs.p2)}>
								<ComboboxInput placeholder='Search accounts...' showTrigger={false} />
							</div>
							<ComboboxStatus>{!searchValue ? 'Start typing to search accounts...' : null}</ComboboxStatus>
							<ComboboxEmpty>No accounts found.</ComboboxEmpty>
							<ComboboxList>
								{(account: IAccount) => (
									<ComboboxItem key={account.id} value={account}>
										{account.companyName}
									</ComboboxItem>
								)}
							</ComboboxList>
						</ComboboxContent>
					</Combobox>
				</BreadcrumbItem>
			</BreadcrumbList>
		</Breadcrumb>
	);
}
